/**
 * Client-side scoring logic.
 *
 * Uses range-based odds bonus model matching the server-side resolve-bets Edge Function.
 *
 * Scoring rules:
 * - Wrong winner: 0 points
 * - Correct winner only: winner_points (default 2)
 * - Correct winner + correct score: winner_points + getOddsBonus(odds_at_bet)
 *
 * Odds bonus brackets (American odds — tuned for DraftKings "Correct Score"
 * market, where every outcome is a positive-odds longshot relative to the
 * series moneyline):
 *   <  +200  → 2   (market favorite outcome)
 *   <  +400  → 3   (likely outcome)
 *   <  +600  → 5   (toss-up)
 *   <  +800  → 7   (mild underdog outcome)
 *   <  +1000 → 9   (underdog outcome)
 *   >= +1000 → 12  (longshot)
 */

interface Bet {
  id: string;
  user_id: string | null;
  series_id: string | null;
  group_id: string | null;
  predicted_winner: string;
  predicted_score: string | null;
  multiplier_at_bet: number | null;
  odds_at_bet: number | null;
  is_correct_winner: boolean | null;
  is_correct_score: boolean | null;
  points_earned: number | null;
}

interface Series {
  id: string;
  status: string | null;
  winner: string | null;
  final_score: string | null;
  round: string | null;
  team_home: string | null;
}

interface LeaderboardEntry {
  id: string;
  user_id: string;
  display_name: string;
  total_points: number;
  correct_winners: number;
  correct_scores: number;
  rank: number;
}

export interface GroupScoringSettings {
  bracket_favorite_strong?: number;  // < +200  (market favorite outcome)
  bracket_favorite_mild?: number;    // +200 to +399
  bracket_pickem?: number;           // +400 to +599
  bracket_underdog_mild?: number;    // +600 to +799
  bracket_underdog?: number;         // +800 to +999
  bracket_longshot?: number;         // +1000 and above
}

const DEFAULT_SETTINGS: Required<GroupScoringSettings> = {
  bracket_favorite_strong: 2,
  bracket_favorite_mild: 3,
  bracket_pickem: 5,
  bracket_underdog_mild: 7,
  bracket_underdog: 9,
  bracket_longshot: 12,
};

/**
 * Fixed base points for predicting the correct series winner.
 * Single source of truth — see CLAUDE.md scoring model.
 * Legacy groups.settings.winner_points (=15) is intentionally ignored;
 * it's leftover seed data from an older scoring model and has no UI to set.
 */
export const WINNER_POINTS = 2;

/**
 * Total points earned for a correct winner + correct exact score.
 * Equals winner_points + getOddsBonus(oddsAtBet).
 */
export function exactScorePoints(
  oddsAtBet: number | null,
  settings?: GroupScoringSettings,
): number {
  return WINNER_POINTS + getOddsBonus(oddsAtBet, settings);
}

/**
 * Returns the odds bonus points for a given American odds value.
 * Falls back to pick'em bracket if oddsAtBet is null.
 */
export function getOddsBonus(oddsAtBet: number | null, settings?: GroupScoringSettings): number {
  const s = { ...DEFAULT_SETTINGS, ...settings };

  if (oddsAtBet === null) return s.bracket_pickem;

  if (oddsAtBet < 200)  return s.bracket_favorite_strong;
  if (oddsAtBet < 400)  return s.bracket_favorite_mild;
  if (oddsAtBet < 600)  return s.bracket_pickem;
  if (oddsAtBet < 800)  return s.bracket_underdog_mild;
  if (oddsAtBet < 1000) return s.bracket_underdog;
  return s.bracket_longshot;
}

/**
 * Parse a score string like "Thunder 4-0", "4-0", or " 4 - 0 " into a numeric tuple.
 * Tolerates optional team prefix and surrounding whitespace. Returns null on failure.
 */
function parseScoreTuple(score: string | null): [number, number] | null {
  if (!score) return null;
  const m = score.trim().match(/(\d+)\s*-\s*(\d+)\s*$/);
  if (!m) return null;
  const a = Number.parseInt(m[1], 10);
  const b = Number.parseInt(m[2], 10);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return [a, b];
}

function namesEqual(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Compare a winner-perspective predicted_score (e.g. "Thunder 4-0") to a
 * home-away final_score (e.g. "4-0"). Converts axes via team_home.
 */
export function isExactScoreMatch(
  predictedScore: string | null,
  finalScore: string | null,
  winner: string | null,
  teamHome: string | null,
): boolean {
  const predicted = parseScoreTuple(predictedScore);
  const final = parseScoreTuple(finalScore);
  if (!predicted || !final || !winner || !teamHome) return false;

  const winnerIsHome = namesEqual(winner, teamHome);
  const finalWinnerFirst: [number, number] = winnerIsHome
    ? [final[0], final[1]]
    : [final[1], final[0]];

  return predicted[0] === finalWinnerFirst[0] && predicted[1] === finalWinnerFirst[1];
}

/**
 * Calculates points earned for a single bet against the resolved series.
 * Range-based scoring: winner_points + odds bonus for correct exact score.
 * Correct winner alone always earns winner_points, even if score is wrong.
 */
export function calculatePoints(
  bet: Bet,
  series: Series,
  settings?: GroupScoringSettings,
): number {
  if (!namesEqual(bet.predicted_winner, series.winner)) return 0;

  const isCorrectScore = isExactScoreMatch(
    bet.predicted_score,
    series.final_score,
    series.winner,
    series.team_home,
  );
  if (!isCorrectScore) return WINNER_POINTS;

  return WINNER_POINTS + getOddsBonus(bet.odds_at_bet, settings);
}

/**
 * Builds a client-side leaderboard from raw bets and series data.
 */
export function calculateClientLeaderboard(
  allBets: Bet[],
  seriesList: Series[],
  memberNames: Record<string, string>, // user_id -> display_name
  settings?: GroupScoringSettings,
): LeaderboardEntry[] {
  const completedSeries = seriesList.filter(s => s.status === 'completed');
  const completedIds = new Set(completedSeries.map(s => s.id));
  const seriesMap = Object.fromEntries(completedSeries.map(s => [s.id, s]));

  // Accumulate per user
  const userStats: Record<string, { points: number; winners: number; scores: number }> = {};

  for (const bet of allBets) {
    if (!bet.user_id || !bet.series_id || !completedIds.has(bet.series_id)) continue;

    const series = seriesMap[bet.series_id];
    if (!series) continue;

    if (!userStats[bet.user_id]) {
      userStats[bet.user_id] = { points: 0, winners: 0, scores: 0 };
    }
    const stats = userStats[bet.user_id];

    const pts = calculatePoints(bet, series, settings);
    stats.points += pts;

    if (namesEqual(bet.predicted_winner, series.winner)) {
      stats.winners += 1;
      if (isExactScoreMatch(bet.predicted_score, series.final_score, series.winner, series.team_home)) {
        stats.scores += 1;
      }
    }
  }

  // Include ALL group members (even those with 0 bets)
  for (const [userId, displayName] of Object.entries(memberNames)) {
    if (!userStats[userId]) {
      userStats[userId] = { points: 0, winners: 0, scores: 0 };
    }
  }

  // Build sorted entries
  const entries = Object.entries(userStats)
    .map(([userId, stats]) => ({
      id: userId,
      user_id: userId,
      display_name: memberNames[userId] || 'שחקן',
      total_points: stats.points,
      correct_winners: stats.winners,
      correct_scores: stats.scores,
      rank: 0,
    }))
    .sort((a, b) => b.total_points - a.total_points);

  entries.forEach((e, i) => { e.rank = i + 1; });

  return entries;
}
